import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pin, X, Plus, Trash2, Upload, Code, ChevronDown } from 'lucide-react';
import WorkflowService from '../../services/workflow.service';
import ConfirmDialog from '../ui/ConfirmDialog';

export default function TestPinsPanel({ workflowId, steps = [], onClose }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStep, setSelectedStep] = useState('');
  const [selectedExecution, setSelectedExecution] = useState('');
  const [manualData, setManualData] = useState('');
  const [addMode, setAddMode] = useState('execution');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, stepId: null });


  
  useEffect(() => {
    if (workflowId) {
      loadPins();
    }
  }, [workflowId]);

  const loadPins = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await WorkflowService.listTestPins(workflowId);
      
      if (result.success) {
        setPins(result.data.pins || []);
      } else {
        setError(result.error || 'Failed to load test pins');
      }
    } catch (err) {
      setError(err.message || 'Failed to load test pins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFromExecution = async () => {
    if (!selectedStep || !selectedExecution) {
      setError('Please select both step and execution');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await WorkflowService.createTestPinFromExecution(
        workflowId,
        selectedStep,
        { execution_id: selectedExecution }
      );

      if (result.success) {
        await loadPins();
        resetForm();
      } else {
        setError(result.error || 'Failed to create pin');
      }
    } catch (err) {
      setError(err.message || 'Failed to create pin');
    } finally {
      setLoading(false);
    }
  };

  const handleAddManual = async () => {
    if (!selectedStep || !manualData) {
      setError('Please select step and provide data');
      return;
    }

    let parsedData;
    try {
      parsedData = JSON.parse(manualData);
    } catch (err) {
      setError('Invalid JSON format');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await WorkflowService.upsertTestPin(
        workflowId,
        selectedStep,
        { pinned_output_data: parsedData }
      );

      if (result.success) {
        await loadPins();
        resetForm();
      } else {
        setError(result.error || 'Failed to create pin');
      }
    } catch (err) {
      setError(err.message || 'Failed to create pin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePin = (stepId) => {
    setConfirmDelete({ open: true, stepId });
  };

  const executeDeletePin = async (stepId) => {
    setConfirmDelete({ open: false, stepId: null });
    setLoading(true);
    setError(null);

    try {
      const result = await WorkflowService.deleteTestPin(workflowId, stepId);

      if (result.success) {
        await loadPins();
      } else {
        setError(result.error || 'Failed to delete pin');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete pin');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setSelectedStep('');
    setSelectedExecution('');
    setManualData('');
    setAddMode('execution');
  };

  const getStepName = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    return step?.name || step?.type || stepId;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Pin className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Test Pins</h2>
              <p className="text-xs text-gray-400">Pin step outputs for consistent testing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Add Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full mb-4 px-4 py-3 border-2 border-dashed border-white/10 hover:border-blue-500/30 rounded-lg text-sm text-gray-400 hover:text-blue-400 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Test Pin
            </button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 rounded-lg bg-white/5 border border-white/10"
            >
              <div className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddMode('execution')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                      addMode === 'execution'
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Upload className="w-3.5 h-3.5 inline mr-1" />
                    From Execution
                  </button>
                  <button
                    onClick={() => setAddMode('manual')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
                      addMode === 'manual'
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Code className="w-3.5 h-3.5 inline mr-1" />
                    Manual JSON
                  </button>
                </div>

                {/* Step Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Select Step
                  </label>
                  <div className="relative">
                    <select
                      value={selectedStep}
                      onChange={(e) => setSelectedStep(e.target.value)}
                      className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm appearance-none focus:outline-none focus:border-blue-500/50"
                    >
                      <option value="">Choose a step...</option>
                      {steps.map((step) => (
                        <option key={step.id} value={step.id}>
                          {step.name || step.type}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Execution ID (if from execution) */}
                {addMode === 'execution' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Execution ID
                    </label>
                    <input
                      type="text"
                      value={selectedExecution}
                      onChange={(e) => setSelectedExecution(e.target.value)}
                      placeholder="Enter execution UUID..."
                      className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                )}

                {/* Manual JSON (if manual) */}
                {addMode === 'manual' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">
                      Output Data (JSON)
                    </label>
                    <textarea
                      value={manualData}
                      onChange={(e) => setManualData(e.target.value)}
                      placeholder={'{\n  "result_data": {...},\n  "output_variables": {...}\n}'}
                      rows={6}
                      className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm font-mono placeholder-gray-600 focus:outline-none focus:border-blue-500/50 resize-none"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={addMode === 'execution' ? handleAddFromExecution : handleAddManual}
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-all"
                  >
                    {loading ? 'Adding...' : 'Add Pin'}
                  </button>
                  <button
                    onClick={resetForm}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Pins List */}
          {loading && pins.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-400">Loading pins...</p>
              </div>
            </div>
          ) : pins.length === 0 && !showAddForm ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mb-4">
                <Pin className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1">No Test Pins</h3>
              <p className="text-xs text-gray-500">Add pins to lock step outputs for consistent testing</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pins.map((pin) => (
                <motion.div
                  key={pin.step_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Pin className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-semibold text-white">
                          {getStepName(pin.step_id)}
                        </span>
                      </div>

                      <div className="text-xs text-gray-400 space-y-1">
                        <div>Step ID: <span className="text-gray-300 font-mono">{pin.step_id}</span></div>
                        {pin.created_at && (
                          <div>Created: <span className="text-gray-300">{new Date(pin.created_at).toLocaleString()}</span></div>
                        )}
                      </div>

                      {pin.pinned_output_data && (
                        <details className="mt-3">
                          <summary className="text-xs text-gray-400 cursor-pointer hover:text-white">
                            View pinned data
                          </summary>
                          <pre className="mt-2 text-xs text-gray-300 bg-black/40 p-3 rounded-lg overflow-x-auto">
                            {JSON.stringify(pin.pinned_output_data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>

                    <button
                      onClick={() => handleDeletePin(pin.step_id)}
                      disabled={loading || confirmDelete.open}
                      className="ml-4 p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        title="Delete Test Pin"
        message="Delete this test pin? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => executeDeletePin(confirmDelete.stepId)}
        onCancel={() => setConfirmDelete({ open: false, stepId: null })}
      />
    </motion.div>
  );
}
